<script setup lang="ts">
import type { NavSection, NavTab } from "~/composables/useContentRoute";
import MegaNavColumns from "./MegaNavColumns.vue";
import MegaNavLinks from "./MegaNavLinks.vue";

// Maps one nav section to an <e-mega-menu-item>. The element owns the
// hover/focus open, the anchored dropdown panel, the single-open invariant, and
// (at the <e-mega-menu> level) the overflow→hamburger + mobile relocation. A
// section with no children is a plain link; with children it gets a
// <e-mega-menu-panel> containing ONLY the nav switch — a tabbed body
// (`section.tabs`) or a flat set of columns. (The title/description header,
// its divider, and the image feature card were intentionally removed.)
const props = defineProps<{ section: NavSection; viewAllLabel?: string }>();

const hasPanel = computed(() => props.section.children.length > 0);

// <e-mega-menu> relocates each <e-mega-menu-panel> out of its item into the
// menu's dropdownContent on connect (relocatePanels). The critical WC defines
// run with `parallel: true`, so that upgrade can win the race against Vue
// hydration and strip the panel from the item's light DOM before Vue walks it
// — "Server rendered element contains fewer child nodes than client vdom".
// Render the panel only after mount: SSR + first client render omit it (match),
// then relocatePanels runs on the resulting slotchange (the post-mount path the
// element is built for).
const isMounted = useIsMounted();

// Pre-built labelled columns when the section provides them, else a single
// unlabelled column over the flat children. Only consulted for a non-tabbed
// panel; a tabbed panel renders `section.tabs` (each tab owns its columns).
const columns = computed(() => {
  const s = props.section;
  if (s.columns?.length) return s.columns;
  return [{ key: "__flat__", label: "", span: 1, items: s.children, indexPath: undefined as string | undefined }];
});

const isMuted = useCanAccess().isMuted;

// A single-column tab flows its items into 3 item tracks instead of one
// stacking column. A tab with 2+ columns keeps one track per column (the element
// auto-derives the track count), so `columns` is only set for the flat
// single-column case.
const itemTracks = 3;
const isFlatTab = (tab: NavTab) => tab.columns.length === 1;
// Preserve the lone column's "view all" target by promoting it to the tab's own
// link when we flatten (the flat list drops the column header + its view-all).
const tabTo = (tab: NavTab) => tab.to ?? (isFlatTab(tab) ? tab.columns[0]?.indexPath : undefined);
</script>

<template>
  <e-mega-menu-item
    :label="section.title"
    :to="hasPanel ? undefined : section.path"
    :data-section-path="section.path"
    :class="!hasPanel && isMuted(section.path) ? 'part-[base]:opacity-60' : undefined"
  >
    <e-mega-menu-panel
      v-if="hasPanel && isMounted"
      :view-all="section.hasIndex ? section.path : undefined"
      :view-all-label="viewAllLabel || 'Read more'"
    >
      <e-mega-menu-tabs v-if="section.tabs?.length">
        <e-mega-menu-tab
          v-for="tab in section.tabs"
          :key="tab.key"
          :label="tab.label"
          :icon="tab.icon || undefined"
          :to="tabTo(tab) || undefined"
          :columns.attr="isFlatTab(tab) ? itemTracks : undefined"
        >
          <MegaNavLinks v-if="isFlatTab(tab)" :items="tab.columns[0]!.items" />
          <MegaNavColumns v-else :columns="tab.columns" :view-all-label="viewAllLabel" />
        </e-mega-menu-tab>
      </e-mega-menu-tabs>

      <MegaNavColumns v-else :columns="columns" :view-all-label="viewAllLabel" />
    </e-mega-menu-panel>
  </e-mega-menu-item>
</template>
